// src/components/CompleteProfile.tsx
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../helpers/Supabase';
import logo from '../../assets/vivvy-logo.png';
import { ChromePicker } from 'react-color';
import { QRCodeCanvas } from 'qrcode.react';

const CompleteProfile: React.FC = () => {
  const history = useHistory();
  const [form, setForm] = useState({
    name: '',
    logo: null as File | null,
    tier: 'free',
    industry: '',
    couponCode: '',
  });
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, size: number, speed: number}>>([]);
  
  // New onboarding flow states
  const [onboardingStep, setOnboardingStep] = useState<'profile' | 'stores' | 'chatbot' | 'qr' | 'complete'>('profile');
  const [clientId, setClientId] = useState<string | null>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [currentStoreIndex, setCurrentStoreIndex] = useState(0);
  
  // Store setup states
  const [storeForm, setStoreForm] = useState({
    store_name: '',
    zip: '',
  });
  
  // Chatbot setup states
  const [chatbotConfig, setChatbotConfig] = useState({
    greeting: '',
    instructions: '',
    positive_prompt: '',
    negative_prompt: '',
  });
  
  // QR page design states
  const [qrDesign, setQrDesign] = useState({
    headline: 'Welcome! Scan to check in',
    overlayMode: 'gradient' as 'solid' | 'gradient',
    solidColor: 'rgba(51, 161, 181, 0.8)',
    gradientStart: 'rgba(51, 161, 181, 0.8)',
    gradientEnd: 'rgba(104, 219, 184, 0.8)',
    fontColor: '#ffffff',
    qrColor: '#ffffff',
    bgType: 'image' as 'image' | 'video',
    bgSourceType: 'url' as 'url' | 'upload',
    bgUrl: 'https://w0.peakpx.com/wallpaper/761/22/HD-wallpaper-blue-background-1-computer-technology-art-background-painting-wide-screen-computer-graphics-abstract-artwork.jpg',
    bgFile: null as File | null,
  });
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  
  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-picker-container')) {
        setShowColorPicker(null);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  // Generate floating particles for extra sexiness
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1,
        speed: Math.random() * 0.5 + 0.1
      }));
      setParticles(newParticles);
    };

    generateParticles();
    const interval = setInterval(() => {
      setParticles(prev => prev.map(p => ({
        ...p,
        y: p.y - p.speed,
        x: p.x + Math.sin(p.y * 0.01) * 0.5
      })).filter(p => p.y > -10).map(p => p.y < -10 ? { ...p, y: 110 } : p));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchIndustryOptions = async () => {
      const { data, error } = await supabase
        .from('industry_templates')
        .select('industry')
        .neq('industry', '')
        .order('industry', { ascending: true });
      if (error) return;
      if (data) {
        const industrySet = new Set<string>();
        data.forEach(d => industrySet.add(d.industry));
        const uniqueIndustries = Array.from(industrySet);
        setIndustryOptions(uniqueIndustries);
      }
    };
    fetchIndustryOptions();
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (PNG, JPG, GIF, etc.)');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File is too large. Please select a file smaller than 10MB.');
        return;
      }
      
      setForm({ ...form, logo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session || !session.user) throw new Error('Not logged in');
  
      const user = session.user;
  
      if (!form.logo) throw new Error('Logo is required');
      if (!form.industry) throw new Error('Please select your industry');
  
            const file = form.logo;
      console.log('📤 Starting logo upload...', file.name, file.size, file.type);
      console.log('📁 File object verification:', {
        constructor: file.constructor.name,
        isFile: file instanceof File,
        isBlob: file instanceof Blob
      });
      
      // Validate file
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('Logo file is too large. Please use a file smaller than 10MB.');
      }
      
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select a valid image file.');
      }
      
      const cleanFileName = file.name.replace(/\s+/g, '_').toLowerCase();
      const filePath = `${Date.now()}-${cleanFileName}`;
      
      console.log('📁 Uploading to path:', filePath);

      // Upload the file using direct fetch to avoid form data wrapping
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) {
        throw new Error('No authentication token available');
      }

      const uploadUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/client-logos/${filePath}`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'Content-Type': file.type || 'image/png',
          'Cache-Control': '3600',
          'x-upsert': 'false'
        },
        body: file
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const uploadData = await response.json();
      console.log('✅ Direct upload successful:', uploadData);
      
      // Use the direct path for the correct URL
      const logoUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/client-logos/${filePath}`;
      console.log('🔗 Logo URL:', logoUrl);
      console.log('🔗 Environment URL:', process.env.REACT_APP_SUPABASE_URL);
      console.log('🔗 Full path from upload:', uploadData.fullPath);
      
      // Test if the file is accessible
      try {
        const response = await fetch(logoUrl);
        console.log('🔍 File accessibility test:', {
          status: response.status,
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length')
        });
        
        if (!response.ok) {
          throw new Error(`File not accessible: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('❌ File accessibility test failed:', error);
        throw new Error('Uploaded file is not accessible. Please try again.');
      }
  
            console.log('💾 Creating client with logo URL:', logoUrl);
      
      // Create new client
      const { data: clientData, error: clientErr } = await supabase
        .from('clients')
        .insert({ 
          name: form.name, 
          location: '', 
          logo_url: logoUrl,
          industry: form.industry
        })
        .select()
        .single();

      if (clientErr) {
        console.error('❌ Client creation error:', clientErr);
        throw clientErr;
      }
      
      console.log('✅ Client created successfully:', clientData);
  
            // Check if user already exists
      console.log('🔍 Checking if user already exists...');
      const { data: existingUsers } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (existingUsers && existingUsers.length > 0) {
        const existingUser = existingUsers[0];
        console.log('⚠️ User already exists:', existingUser);
        
        if (existingUser.client_id) {
          console.log('✅ Using existing user with client_id:', existingUser.client_id);
          setClientId(existingUser.client_id);
          setOnboardingStep('stores');
          setLoading(false);
          return;
        } else {
          console.log('🔄 Updating existing user with new client_id:', clientData.id);
          // Update existing user with client_id
          const { error: updateError } = await supabase
            .from('users')
            .upsert({
              id: existingUser.id,
              auth_user_id: user.id,
              client_id: clientData.id,
              role: 'admin',
              tier: form.tier,
              created_at: existingUser.created_at
            }, {
              onConflict: 'id'
            });
          
          if (updateError) {
            console.error('User update error:', updateError);
            throw updateError;
          }
          
          setClientId(clientData.id);
          setOnboardingStep('stores');
          setLoading(false);
          return;
        }
      }
      
      console.log('🔄 Creating user record...');
      console.log('🔄 Auth user ID:', user.id);
      console.log('🔄 Client ID:', clientData.id);
      
      const { data: userInsertData, error: userInsertError } = await supabase.from('users').insert({
        auth_user_id: user.id,
        client_id: clientData.id,
        role: 'admin',
        tier: form.tier,
        created_at: new Date().toISOString(),
      }).select();

      if (userInsertError) {
        console.error('User insert error:', userInsertError);
        throw userInsertError;
      }

      if (!userInsertData || userInsertData.length === 0) {
        console.error('User insert succeeded but no data returned');
        throw new Error('User creation failed - no data returned');
      }

      console.log('✅ User created successfully with client_id:', clientData.id);
      console.log('📋 User insert response:', userInsertData);
      console.log('📋 User insert response type:', typeof userInsertData);
      console.log('📋 User insert response length:', userInsertData?.length);
      if (userInsertData && userInsertData.length > 0) {
        console.log('📋 First user in response:', userInsertData[0]);
        console.log('📋 User auth_user_id:', userInsertData[0]?.auth_user_id);
        console.log('📋 User client_id:', userInsertData[0]?.client_id);
        console.log('📋 Current auth user ID:', user.id);
        console.log('📋 Do they match?', userInsertData[0]?.auth_user_id === user.id);
      }
      setClientId(clientData.id);
      setOnboardingStep('stores');
      setLoading(false);
      
    } catch (err: any) {
      console.error('[Setup Error]', err.message || err);
      setError(err.message || 'Something went wrong.');
      setLoading(false);
    }
  };

  const handleStoreSubmit = async () => {
    if (!storeForm.store_name || !storeForm.zip) {
      setError('Store name and ZIP code are required');
      return;
    }

    try {
      // Generate hash_id for the store
      const hash_id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      
      const { data: storeData, error: storeError } = await supabase
        .from('stores')
        .insert({
          client_id: clientId,
          store_name: storeForm.store_name,
          zip: storeForm.zip,
          hash_id,
          is_active: true, // Always create stores as active
        })
        .select()
        .single();

      if (storeError) throw storeError;

      setStores([...stores, storeData]);
      
      // Check if we need to add more stores based on tier
      const storeLimit = form.tier === 'free' ? 1 : 5; // Free gets 1, others get 5 for demo
      
      if (stores.length + 1 < storeLimit) {
        // Add another store
        setStoreForm({ store_name: '', zip: '' });
        setCurrentStoreIndex(currentStoreIndex + 1);
      } else {
        // Move to chatbot setup
        setOnboardingStep('chatbot');
        // Auto-fill chatbot config based on industry
        await loadIndustryTemplate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create store');
    }
  };

  const loadIndustryTemplate = async () => {
    if (!form.industry) return;
    
    const { data: template } = await supabase
      .from('industry_templates')
      .select('*')
      .eq('industry', form.industry)
      .eq('flow_type', 'survey')
      .maybeSingle();

    if (template) {
      setChatbotConfig({
        greeting: template.greeting || 'Welcome! How was your experience today?',
        instructions: template.gpt_instructions || 'Be friendly and helpful. Ask about their experience.',
        positive_prompt: template.positive_prompt || 'Would you like to leave a review on Google?',
        negative_prompt: template.negative_prompt || 'We\'re sorry to hear that. Can you tell us more?',
      });
    }
  };

  const handleChatbotSubmit = async () => {
    try {
      // Save chatbot config for each store
      for (const store of stores) {
        await supabase
          .from('chatbot_flows')
          .upsert([{
            store_id: store.id,
            flow_type: 'survey',
            greeting: chatbotConfig.greeting,
            gpt_instructions: chatbotConfig.instructions,
            follow_up_logic: {
              positive: { prompt: chatbotConfig.positive_prompt },
              negative: { prompt: chatbotConfig.negative_prompt },
            },
          }], {
            onConflict: 'store_id,flow_type'
          });
      }
      
      setOnboardingStep('qr');
    } catch (err: any) {
      setError(err.message || 'Failed to save chatbot config');
    }
  };

  const handleQRSubmit = async () => {
    try {
      // Save QR design config for each store using upsert to avoid CORS PATCH issues
      for (const store of stores) {
        await supabase
          .from('stores')
          .upsert({
            id: store.id,
            store_name: store.store_name,
            client_id: store.client_id,
            hash_id: store.hash_id,
            industry: store.industry,
            is_active: store.is_active,
            design_config: {
              headline: qrDesign.headline,
              overlayMode: qrDesign.overlayMode,
              solidColor: qrDesign.solidColor,
              gradientStart: qrDesign.gradientStart,
              gradientEnd: qrDesign.gradientEnd,
              fontColor: qrDesign.fontColor,
              qrColor: qrDesign.qrColor,
            },
          }, {
            onConflict: 'id'
          });
      }
      
      setOnboardingStep('complete');
      
      console.log('🎉 Onboarding complete! Verifying user setup...');
      
      // Verify user was created properly before redirecting with retry mechanism
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        // Retry mechanism to handle database timing issues
        let verifyUser = null;
        let retryCount = 0;
        const maxRetries = 5;
        
        while (!verifyUser && retryCount < maxRetries) {
          console.log(`🔍 Verification attempt ${retryCount + 1}/${maxRetries}...`);
          console.log('🔍 Looking for user with auth_user_id:', authUser.id);
          
          // Try a broader query first to see what's in the users table
          const { data: allUsers, error: allUsersError } = await supabase
            .from('users')
            .select('*')
            .limit(5);
          
          console.log('🔍 All users in table:', allUsers);
          console.log('🔍 All users error:', allUsersError);
          
          // Use a different approach to handle multiple rows
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('auth_user_id', authUser.id)
            .order('created_at', { ascending: false })
            .limit(1);
          
          console.log('🔍 Specific user query result:', userData);
          console.log('🔍 Specific user query error:', userError);
          
          verifyUser = userData && userData.length > 0 ? userData[0] : null;
          
          if (!verifyUser) {
            console.log('⏳ User record not found yet, waiting 1 second...');
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
            retryCount++;
          }
        }
        
        console.log('🔍 Final verification - User record:', verifyUser);
        
        if (verifyUser?.client_id) {
          console.log('✅ User verification successful! Redirecting to dashboard...');
          setTimeout(() => {
            console.log('🚀 Redirecting to dashboard now');
            history.push('/dashboard');
          }, 2000);
        } else {
          console.log('❌ User verification failed after all retries!');
          console.log('⚠️ However, we know the user was created successfully, so attempting redirect anyway...');
          
          // Since we know the user was created (we have the insert response), 
          // let's try redirecting anyway after a longer delay
          setTimeout(() => {
            console.log('🚀 Attempting redirect despite verification failure...');
            history.push('/dashboard');
          }, 3000);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save QR design');
    }
  };

  const getProgressPercentage = () => {
    switch (onboardingStep) {
      case 'profile': return 20;
      case 'stores': return 40;
      case 'chatbot': return 60;
      case 'qr': return 80;
      case 'complete': return 100;
      default: return 0;
    }
  };

  const isFormValid = form.name && form.industry && form.logo;

  const getPricingInfo = (tier: string) => {
    switch(tier) {
      case 'free':
        return { price: '$0', period: 'forever', savings: null };
      case 'monthly':
        return { price: '$60', period: '/store/month', savings: null };
      case 'annual':
        return { price: '$400', period: '/store/year', savings: 'Save 44%' };
      default:
        return { price: '$0', period: 'forever', savings: null };
    }
  };

  const getStoreLimit = () => {
    switch(form.tier) {
      case 'free': return 1;
      case 'monthly': return 5;
      case 'annual': return 5;
      default: return 1;
    }
  };

  const renderProfileStep = () => (
    <div className="complete-profile-box">
      {/* Header */}
      <div className="profile-header">
        <img src={logo} alt="Vivvy Logo" className="profile-logo" />
        <h1>🎉 Welcome to Vivvy!</h1>
        <p>Let's set up your business profile in just a few steps</p>
        <div className="sparkle-effect">✨</div>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
        <span className="progress-text">{getProgressPercentage()}% Complete</span>
      </div>

      {/* Form Steps */}
      <div className="form-steps">
        {/* Step 1: Business Name */}
        <div className={`form-step ${currentStep >= 1 ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">1</div>
            <div className="step-info">
              <h3>Business Name</h3>
              <p>What's your business called?</p>
            </div>
          </div>
          <div className="input-group">
            <input 
              id="business-name"
              name="name" 
              type="text"
              placeholder="Enter your business name" 
              value={form.name}
              onChange={handleInput}
              className="modern-input"
            />
          </div>
        </div>

        {/* Step 2: Industry */}
        <div className={`form-step ${currentStep >= 2 ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">2</div>
            <div className="step-info">
              <h3>Industry</h3>
              <p>What type of business is this?</p>
            </div>
          </div>
          <div className="input-group">
            <select 
              id="industry-select"
              name="industry" 
              value={form.industry}
              onChange={handleInput}
              className="modern-select"
              aria-label="Select your industry"
            >
              <option value="">Select your industry...</option>
              {industryOptions.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
            <div className="helper-text">
              This helps us customize your experience with industry-specific templates
            </div>
          </div>
        </div>

        {/* Step 3: Logo Upload */}
        <div className={`form-step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">3</div>
            <div className="step-info">
              <h3>Business Logo</h3>
              <p>Upload your company logo</p>
            </div>
          </div>
          <div className="logo-upload-container">
            {logoPreview ? (
              <div className="logo-preview">
                <img src={logoPreview} alt="Logo Preview" />
                <button 
                  type="button" 
                  onClick={() => {
                    setForm({ ...form, logo: null });
                    setLogoPreview(null);
                  }}
                  className="change-logo-btn"
                >
                  Change Logo
                </button>
              </div>
            ) : (
              <div className="logo-upload-area">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileUpload}
                  id="logo-upload"
                  className="logo-input"
                />
                <label htmlFor="logo-upload" className="upload-label">
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    <strong>Click to upload</strong>
                    <span>or drag and drop</span>
                    <span className="file-types">PNG, JPG, SVG up to 10MB</span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Step 4: Plan Selection */}
        <div className={`form-step ${currentStep >= 4 ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">4</div>
            <div className="step-info">
              <h3>Choose Your Plan</h3>
              <p>Select the plan that fits your needs</p>
            </div>
          </div>
          <div className="plans-container">
            <div 
              className={`plan-card ${form.tier === 'free' ? 'selected' : ''}`} 
              onClick={() => setForm({ ...form, tier: 'free' })}
            >
              <div className="plan-header">
                <div className="plan-icon">🚀</div>
                <h3>Free Trial</h3>
                <div className="plan-price">$0</div>
                <div className="plan-period">forever</div>
              </div>
              <div className="plan-features">
                <div className="feature">✓ Basic analytics</div>
                <div className="feature">✓ 1 location</div>
                <div className="feature">✓ Core features</div>
              </div>
              <div className="plan-badge">Perfect to start</div>
            </div>
            
            <div 
              className={`plan-card ${form.tier === 'monthly' ? 'selected' : ''}`} 
              onClick={() => setForm({ ...form, tier: 'monthly' })}
            >
              <div className="plan-header">
                <div className="plan-icon">⭐</div>
                <h3>Monthly Pro</h3>
                <div className="plan-price">$60</div>
                <div className="plan-period">/store/month</div>
              </div>
              <div className="plan-features">
                <div className="feature">✓ Unlimited locations</div>
                <div className="feature">✓ AI chatbot access</div>
                <div className="feature">✓ Advanced analytics</div>
                <div className="feature">✓ Priority support</div>
              </div>
              <div className="plan-badge">Most flexible</div>
            </div>

            <div 
              className={`plan-card ${form.tier === 'annual' ? 'selected' : ''}`} 
              onClick={() => setForm({ ...form, tier: 'annual' })}
            >
              <div className="plan-header">
                <div className="plan-icon">💎</div>
                <h3>Annual Pro</h3>
                <div className="plan-price">$400</div>
                <div className="plan-period">/store/year</div>
                {getPricingInfo('annual').savings && (
                  <div className="savings-badge">{getPricingInfo('annual').savings}</div>
                )}
              </div>
              <div className="plan-features">
                <div className="feature">✓ Everything in Monthly</div>
                <div className="feature">✓ 44% savings</div>
                <div className="feature">✓ Early access features</div>
                <div className="feature">✓ VIP support</div>
              </div>
              <div className="plan-badge">Best value</div>
            </div>
          </div>

          {/* Coupon Code Input */}
          <div className="coupon-section">
            <div className="input-group">
              <label htmlFor="coupon-code">Have a coupon code?</label>
              <div className="coupon-input-container">
                <input 
                  id="coupon-code"
                  name="couponCode" 
                  type="text"
                  placeholder="Enter coupon code" 
                  value={form.couponCode}
                  onChange={handleInput}
                  className="coupon-input"
                />
                <button 
                  type="button" 
                  className="apply-coupon-btn"
                  onClick={() => {
                    // Add coupon validation logic here
                    console.log('Applying coupon:', form.couponCode);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        {currentStep > 1 && (
          <button 
            onClick={() => setCurrentStep(currentStep - 1)}
            className="btn-secondary"
            disabled={loading}
          >
            ← Previous
          </button>
        )}
        
        {currentStep < 4 ? (
          <button 
            onClick={() => setCurrentStep(currentStep + 1)}
            className="btn-primary"
            disabled={loading}
          >
            Next →
          </button>
        ) : (
          <button 
            onClick={handleProfileSubmit}
            className="btn-success"
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Setting up your account...
              </>
            ) : (
              <>
                🎉 Complete Profile Setup
              </>
            )}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}
    </div>
  );

  const renderStoreStep = () => (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal store-setup-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>🏪 Store Setup</h2>
            <p>Let's add your first {getStoreLimit()} store{getStoreLimit() > 1 ? 's' : ''}</p>
          </div>
          <div className="store-counter">
            <span className="current">{currentStoreIndex + 1}</span>
            <span className="separator">/</span>
            <span className="total">{getStoreLimit()}</span>
          </div>
        </div>

        <div className="modal-content">
          <div className="store-form">
            <div className="input-group">
              <label>Store Name</label>
              <input 
                type="text"
                value={storeForm.store_name}
                onChange={(e) => setStoreForm({...storeForm, store_name: e.target.value})}
                placeholder="e.g., Downtown Location"
                className="modern-input"
              />
            </div>
            
            <div className="input-group">
              <label>ZIP Code</label>
              <input 
                type="text"
                value={storeForm.zip}
                onChange={(e) => setStoreForm({...storeForm, zip: e.target.value})}
                placeholder="12345"
                className="modern-input"
              />
            </div>
            

          </div>

          {stores.length > 0 && (
            <div className="stores-preview">
              <h4>✅ Stores Added:</h4>
              <div className="stores-list">
                {stores.map((store, index) => (
                  <div key={store.id} className="store-item">
                    <span className="store-name">{store.store_name}</span>
                    <span className="store-location">{store.zip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            onClick={handleStoreSubmit}
            className="btn-primary"
            disabled={!storeForm.store_name || !storeForm.zip}
          >
            {currentStoreIndex + 1 < getStoreLimit() ? 'Add Store & Continue' : 'Finish Store Setup'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderChatbotStep = () => (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal chatbot-setup-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>🤖 Chatbot Setup</h2>
            <p>Configure your AI chatbot for all stores</p>
          </div>
          <div className="setup-icon">🤖</div>
        </div>

        <div className="modal-content">
          <div className="chatbot-form">
            <div className="input-group">
              <label>Welcome Message</label>
              <input 
                type="text"
                value={chatbotConfig.greeting}
                onChange={(e) => setChatbotConfig({...chatbotConfig, greeting: e.target.value})}
                placeholder="Welcome! How was your experience today?"
                className="modern-input"
              />
              <div className="helper-text">This is the first message customers see</div>
            </div>
            
            <div className="input-group">
              <label>AI Instructions</label>
              <textarea 
                value={chatbotConfig.instructions}
                onChange={(e) => setChatbotConfig({...chatbotConfig, instructions: e.target.value})}
                placeholder="Be friendly and helpful. Ask about their experience..."
                className="modern-textarea"
                rows={4}
              />
              <div className="helper-text">Tell the AI how to behave and respond</div>
            </div>
            
            <div className="input-group">
              <label>Happy Customer Response</label>
              <input 
                type="text"
                value={chatbotConfig.positive_prompt}
                onChange={(e) => setChatbotConfig({...chatbotConfig, positive_prompt: e.target.value})}
                placeholder="Would you like to leave a review on Google?"
                className="modern-input"
              />
              <div className="helper-text">What to ask when customers are happy</div>
            </div>
            
            <div className="input-group">
              <label>Unhappy Customer Response</label>
              <input 
                type="text"
                value={chatbotConfig.negative_prompt}
                onChange={(e) => setChatbotConfig({...chatbotConfig, negative_prompt: e.target.value})}
                placeholder="We're sorry to hear that. Can you tell us more?"
                className="modern-input"
              />
              <div className="helper-text">What to ask when customers are unhappy</div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            onClick={handleChatbotSubmit}
            className="btn-primary"
            disabled={!chatbotConfig.greeting || !chatbotConfig.instructions}
          >
            Save Chatbot & Continue
          </button>
        </div>
      </div>
    </div>
  );

  const renderQRStep = () => (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal qr-setup-modal fullscreen-qr-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>🎨 QR Page Designer</h2>
            <p>Create your custom check-in display</p>
          </div>
          <div className="setup-icon">🎨</div>
        </div>

        <div className="modal-content">
          <div className="qr-designer">
            <div className="designer-panel">
              <div className="panel-section">
                <h3>📝 Headline Text</h3>
                <input 
                  type="text"
                  value={qrDesign.headline}
                  onChange={(e) => setQrDesign({...qrDesign, headline: e.target.value})}
                  placeholder="Welcome! Scan to check in"
                  className="modern-input"
                />
                <p className="helper-text">This is the main message customers will see</p>
              </div>
              
              <div className="panel-section">
                <h3>🖼️ Background Type</h3>
                <div className="option-group">
                  <button 
                    className={`option-btn ${qrDesign.bgType === 'image' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, bgType: 'image'})}
                  >
                    📷 Image
                  </button>
                  <button 
                    className={`option-btn ${qrDesign.bgType === 'video' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, bgType: 'video'})}
                  >
                    🎥 Video
                  </button>
                </div>
              </div>

              <div className="panel-section">
                <h3>📁 Background Source</h3>
                <div className="option-group">
                  <button 
                    className={`option-btn ${qrDesign.bgSourceType === 'url' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, bgSourceType: 'url'})}
                  >
                    🔗 URL
                  </button>
                  <button 
                    className={`option-btn ${qrDesign.bgSourceType === 'upload' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, bgSourceType: 'upload'})}
                  >
                    📤 Upload
                  </button>
                </div>

                {qrDesign.bgSourceType === 'url' ? (
                  <input 
                    type="text" 
                    value={qrDesign.bgUrl} 
                    onChange={(e) => setQrDesign({...qrDesign, bgUrl: e.target.value})}
                    placeholder={`Enter ${qrDesign.bgType} URL...`}
                    className="modern-input"
                  />
                ) : (
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept={qrDesign.bgType === "image" ? "image/*" : "video/*"}
                      onChange={(e) => setQrDesign({...qrDesign, bgFile: e.target.files?.[0] || null})}
                      id="bg-upload"
                    />
                    <label htmlFor="bg-upload">
                      📁 Choose {qrDesign.bgType === 'image' ? 'Image' : 'Video'} File
                    </label>
                  </div>
                )}
              </div>
              
              <div className="panel-section">
                <h3>🎨 Overlay Style</h3>
                <div className="option-group">
                  <button 
                    className={`option-btn ${qrDesign.overlayMode === 'solid' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, overlayMode: 'solid'})}
                  >
                    🎯 Solid
                  </button>
                  <button 
                    className={`option-btn ${qrDesign.overlayMode === 'gradient' ? 'active' : ''}`}
                    onClick={() => setQrDesign({...qrDesign, overlayMode: 'gradient'})}
                  >
                    🌈 Gradient
                  </button>
                </div>
              </div>
              
              <div className="panel-section">
                <h3>🎨 Color Settings</h3>
                <div className="color-controls">
                  <div className="color-item">
                    <label>Text Color</label>
                    <div className="color-picker-container">
                      <div 
                        className="color-preview"
                        style={{ backgroundColor: qrDesign.fontColor }}
                        onClick={() => setShowColorPicker(showColorPicker === 'font' ? null : 'font')}
                      ></div>
                      {showColorPicker === 'font' && (
                        <div className="color-picker-popup">
                          <ChromePicker
                            color={qrDesign.fontColor}
                            onChange={(color) => setQrDesign({...qrDesign, fontColor: color.hex})}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="color-item">
                    <label>QR Code Color</label>
                    <div className="color-picker-container">
                      <div 
                        className="color-preview"
                        style={{ backgroundColor: qrDesign.qrColor }}
                        onClick={() => setShowColorPicker(showColorPicker === 'qr' ? null : 'qr')}
                      ></div>
                      {showColorPicker === 'qr' && (
                        <div className="color-picker-popup">
                          <ChromePicker
                            color={qrDesign.qrColor}
                            onChange={(color) => setQrDesign({...qrDesign, qrColor: color.hex})}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {qrDesign.overlayMode === 'solid' ? (
                    <div className="color-item">
                      <label>Overlay Color</label>
                      <div className="color-picker-container">
                        <div 
                          className="color-preview"
                          style={{ backgroundColor: qrDesign.solidColor }}
                          onClick={() => setShowColorPicker(showColorPicker === 'overlay' ? null : 'overlay')}
                        ></div>
                        {showColorPicker === 'overlay' && (
                          <div className="color-picker-popup">
                            <ChromePicker
                              color={qrDesign.solidColor}
                              onChange={(color) => setQrDesign({...qrDesign, solidColor: color.hex})}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="color-item">
                        <label>Gradient Start</label>
                        <div className="color-picker-container">
                          <div 
                            className="color-preview"
                            style={{ backgroundColor: qrDesign.gradientStart }}
                            onClick={() => setShowColorPicker(showColorPicker === 'gradient1' ? null : 'gradient1')}
                          ></div>
                          {showColorPicker === 'gradient1' && (
                            <div className="color-picker-popup">
                              <ChromePicker
                                color={qrDesign.gradientStart}
                                onChange={(color) => setQrDesign({...qrDesign, gradientStart: color.hex})}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="color-item">
                        <label>Gradient End</label>
                        <div className="color-picker-container">
                          <div 
                            className="color-preview"
                            style={{ backgroundColor: qrDesign.gradientEnd }}
                            onClick={() => setShowColorPicker(showColorPicker === 'gradient2' ? null : 'gradient2')}
                          ></div>
                          {showColorPicker === 'gradient2' && (
                            <div className="color-picker-popup">
                              <ChromePicker
                                color={qrDesign.gradientEnd}
                                onChange={(color) => setQrDesign({...qrDesign, gradientEnd: color.hex})}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="preview-panel">
              <h4>📱 Live Preview</h4>
              <div className="qr-preview">
                <div className="checkin-preview">
                  {qrDesign.bgType === "image" ? (
                    <img
                      src={qrDesign.bgSourceType === 'upload' && qrDesign.bgFile 
                        ? URL.createObjectURL(qrDesign.bgFile)
                        : qrDesign.bgUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <video
                      autoPlay
                      loop
                      muted
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    >
                      <source src={qrDesign.bgSourceType === 'upload' && qrDesign.bgFile 
                        ? URL.createObjectURL(qrDesign.bgFile)
                        : qrDesign.bgUrl} type="video/mp4" />
                    </video>
                  )}

                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: qrDesign.overlayMode === 'solid' 
                        ? qrDesign.solidColor 
                        : `linear-gradient(135deg, ${qrDesign.gradientStart}, ${qrDesign.gradientEnd})`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: qrDesign.fontColor,
                      textAlign: 'center',
                      padding: '20px'
                    }}
                  >
                    <img
                      src={logoPreview || logo}
                      alt="Logo"
                      style={{
                        width: '120px',
                        marginBottom: '20px',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
                      }}
                    />
                    <h2
                      style={{
                        fontSize: '2rem',
                        marginBottom: '20px',
                        animation: 'fadeInUp 0.8s ease-out',
                        color: qrDesign.fontColor,
                      }}
                    >
                      {qrDesign.headline}
                    </h2>
                    <QRCodeCanvas
                      value="https://app.vivvy.ai/checkin/demo"
                      size={140}
                      fgColor={qrDesign.qrColor}
                      bgColor="transparent"
                      style={{
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                      }}
                    />
                    <p
                      style={{
                        marginTop: '10px',
                        fontSize: '0.9rem',
                        color: qrDesign.fontColor,
                      }}
                    >
                      Powered by Vivvy
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            onClick={handleQRSubmit}
            className="btn-primary"
          >
            Save Design & Complete Setup
          </button>
        </div>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal complete-modal">
        <div className="modal-header">
          <div className="modal-title">
            <h2>🎉 Setup Complete!</h2>
            <p>You're all set up and ready to go</p>
          </div>
        </div>

        <div className="modal-content">
          <div className="success-animation">
            <div className="success-icon">✨</div>
            <div className="success-message">
              <h3>Welcome to Vivvy!</h3>
              <p>Your business is now configured with:</p>
              <ul>
                <li>✅ {stores.length} store{stores.length > 1 ? 's' : ''} set up</li>
                <li>✅ AI chatbot configured</li>
                <li>✅ QR check-in pages designed</li>
                <li>✅ Ready to collect feedback</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <p>Redirecting to your dashboard...</p>
        </div>
      </div>
    </div>
  );

  // Render based on current onboarding step
  if (onboardingStep === 'profile') {
    return (
      <div className="complete-profile-container">
        {/* Floating Particles */}
        <div className="floating-particles">
          {particles.map(particle => (
            <div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
              }}
            />
          ))}
        </div>
        {renderProfileStep()}
      </div>
    );
  }

  // Render modal overlays for other steps
  return (
    <div className="complete-profile-container">
      {onboardingStep === 'stores' && renderStoreStep()}
      {onboardingStep === 'chatbot' && renderChatbotStep()}
      {onboardingStep === 'qr' && renderQRStep()}
      {onboardingStep === 'complete' && renderCompleteStep()}
    </div>
  );
};

export default CompleteProfile;